<script>
	import { createEventDispatcher, setContext } from 'svelte';
	import { writable } from 'svelte/store';

	export let align = 'center';
	export let panes = [];

	const dispatch = createEventDispatcher();
	const registeredPanes = [];
	let paneList = [];
	let selectedIndex = 0;
	let activeIndex = 0;
	const activeIndexStore = writable(0);

	const tabContext = {
		register(pane) {
			pane.index = registeredPanes.length;
			registeredPanes.push(pane);
			paneList = [...registeredPanes];
			return () => {
				const index = registeredPanes.indexOf(pane);
				if (index !== -1) {
					registeredPanes.splice(index, 1);
					registeredPanes.forEach((item, itemIndex) => item.index = itemIndex);
					paneList = [...registeredPanes];
				}
			};
		},
		update() {
			paneList = [...registeredPanes];
		},
		activeIndex: activeIndexStore,
		select(index) {
			if (!visiblePanes[index]?.disabled) {
				selectedIndex = index;
				activeIndexStore.set(index);
				dispatch('select', { index, title: visiblePanes[index].title });
			}
		}
	};

	setContext('ilb-tab-view', tabContext);

	$: visiblePanes = panes.length ? panes : paneList;
	$: defaultIndex = visiblePanes.findIndex((pane) => pane.isActive === true);
	$: fallbackIndex = defaultIndex >= 0 ? defaultIndex : 0;
	$: selectedIndex = Math.min(selectedIndex, Math.max(visiblePanes.length - 1, 0));
	$: activeIndex = visiblePanes.length ? selectedIndex || fallbackIndex : 0;
	$: activeIndexStore.set(activeIndex);
	$: if (!visiblePanes.length) {
		selectedIndex = 0;
	}
</script>

<div class="tab-view" class:--left={align === 'left'} class:--right={align === 'right'}>
	<div class="tab-bar" role="tablist" aria-label="Разделы">
		{#each visiblePanes as pane, index}
			<button
				class="tab"
				class:--active={activeIndex === index}
				class:--disabled={pane.disabled}
				type="button"
				role="tab"
				aria-selected={activeIndex === index}
				aria-controls={pane.panelId}
				disabled={pane.disabled}
				on:click={() => tabContext.select(index)}
			>
				{pane.title}
			</button>
		{/each}
	</div>

	<div class="tab-content">
		{#if panes.length}
			{#each visiblePanes as pane, index}
				{#if activeIndex === index}
					<div class="tab-pane" role="tabpanel" id={pane.panelId} aria-hidden="false">
						{@html pane.content}
					</div>
				{/if}
			{/each}
		{:else}
			<slot />
		{/if}
	</div>
</div>

<style>
	:host {
		display: block;
	}

	.tab-view {
		width: 100%;
		margin: 0 auto;
	}

	.tab-bar {
		display: flex;
		justify-content: center;
		gap: 28px;
		border-bottom: 1px solid #d7dce2;
		margin-top: 20px;
	}

	.tab-view.--left .tab-bar {
		justify-content: flex-start;
	}

	.tab-view.--right .tab-bar {
		justify-content: flex-end;
	}

	.tab {
		position: relative;
		padding: 0 0 11px;
		border: 0;
		background: transparent;
		color: #53657b;
		font: inherit;
		cursor: pointer;
		transition: color 160ms ease;
	}

	.tab::after {
		position: absolute;
		right: 0;
		bottom: -1px;
		left: 0;
		height: 2px;
		background: #00244d;
		content: '';
		transform: scaleX(0);
		transition: transform 160ms ease;
	}

	.tab:hover,
	.tab.--active {
		color: #00244d;
	}

	.tab.--active {
		font-weight: 600;
	}

	.tab.--active::after {
		transform: scaleX(1);
	}

	.tab.--disabled {
		cursor: not-allowed;
		opacity: 0.45;
	}

	.tab-content {
		padding-top: 24px;
	}
</style>
